import { toast } from 'react-toastify';
import { i18n } from '../translate/i18n';
import isString from 'lodash/isString';

/**
 * Exibe erro em toast (react-toastify). Visual e opções vêm do ToastContainer.
 * Usado para erros de API/backend: extrai message do response, fallback em i18n ou mensagem genérica.
 *
 * Padrão do projeto:
 * - Em catch de chamadas API: usar toastError(err).
 * - Para sucesso/validação/feedback fixo: usar toastSuccess(), toastWarning(), toastInfo() ou toast.error(msg) direto.
 * - Não usar em componentes de conversa (WhatsApp/Face/Insta) — lá há NotificationToast próprio.
 */
/**
 * Códigos que já têm aviso fixo próprio na UI — o toast só repetiria a mensagem,
 * uma vez por chamada bloqueada. ERR_TRIAL_EXPIRED aparece na barra do topo
 * (TrialBanner) enquanto a conta segue sem plano contratado.
 */
const SILENCED_BACKEND_ERROR_CODES = new Set(['ERR_TRIAL_EXPIRED']);

/** Extrai código ERR_* de `message` quando o backend envia só o código ou texto repetido/misto. */
const extractKnownBackendErrorCode = (message) => {
  if (!isString(message)) return null;
  const trimmed = message.trim();
  if (i18n.exists(`backendErrors.${trimmed}`)) return trimmed;
  const match = trimmed.match(/\b(ERR_[A-Z0-9_]+)\b/);
  if (match && i18n.exists(`backendErrors.${match[1]}`)) return match[1];
  return null;
};

const extractAnyBackendErrorCode = (message) => {
  if (!isString(message)) return null;
  const trimmed = message.trim();
  if (/^ERR_[A-Z0-9_]+$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/\b(ERR_[A-Z0-9_]+)\b/);
  return match ? match[1] : null;
};

const translateBackendErrorToken = (token) => {
  const key = `backendErrorTokens.${token}`;
  if (i18n.exists(key)) return i18n.t(key);
  return token.toLowerCase();
};

const humanizeBackendErrorCode = (code) => {
  if (!isString(code)) return null;
  const normalized = code.trim();
  if (!/^ERR_[A-Z0-9_]+$/.test(normalized)) return null;
  const tokens = normalized
    .replace(/^ERR_/, '')
    .split('_')
    .filter(Boolean)
    .map((t) => translateBackendErrorToken(t));

  if (tokens.length === 0) return null;
  const sentence = tokens.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const extractResponseMessage = (data) => {
  if (!data) return null;
  if (isString(data)) {
    const trimmed = data.trim();
    return trimmed || null;
  }
  if (isString(data.message)) {
    const trimmed = data.message.trim();
    return trimmed || null;
  }
  if (Array.isArray(data.message)) {
    const parts = data.message
      .filter(isString)
      .map((item) => item.trim())
      .filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  if (isString(data.error)) {
    const trimmed = data.error.trim();
    return trimmed || null;
  }
  if (Array.isArray(data.errors)) {
    const first = data.errors[0];
    if (isString(first)) return first.trim() || null;
    if (first && isString(first.message)) return first.message.trim() || null;
  }
  return null;
};

const isCancelledRequest = (err) => err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';

const resolveHttpStatusMessage = (status) => {
  if (!status) return null;
  if (status === 403) return i18n.t('toastErrors.forbidden');
  if (status === 404) return i18n.t('toastErrors.notFound');
  if (status >= 500) return i18n.t('toastErrors.serverError', { status });
  if (status >= 400) return i18n.t('toastErrors.httpError', { status });
  return null;
};

const resolveFallbackErrorMessage = (err) => {
  const statusFromResponse = err?.response?.status;
  const statusFromAxiosMessage = isString(err?.message)
    ? err.message.match(/^Request failed with status code (\d+)$/)
    : null;
  const httpStatus =
    statusFromResponse || (statusFromAxiosMessage && Number(statusFromAxiosMessage[1]));

  const statusMessage = resolveHttpStatusMessage(httpStatus);
  if (statusMessage) return statusMessage;

  if (!err?.response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error')) {
    return i18n.t('toastErrors.network');
  }

  if (err?.code === 'ECONNABORTED' || (isString(err?.message) && /timeout/i.test(err.message))) {
    return i18n.t('toastErrors.timeout');
  }

  const responseMessage = extractResponseMessage(err?.response?.data);
  if (responseMessage) return responseMessage;

  if (isString(err?.message) && err.message !== 'Network Error') {
    return err.message;
  }

  return i18n.t('toastErrors.generic');
};

const toastError = (err) => {
  if (err?.isHeaderOrCookieTooLarge) {
    return;
  }
  if (isCancelledRequest(err)) {
    return;
  }
  const data = err?.response?.data;
  const backendMessage = extractResponseMessage(data);
  const rawError = data?.error ?? data?.message ?? data?.code;

  let errorCode =
    (isString(rawError) && rawError) ||
    (rawError && isString(rawError.code) && rawError.code) ||
    (rawError && isString(rawError.error) && rawError.error) ||
    (rawError && isString(rawError.message) && rawError.message) ||
    null;

  if (isString(errorCode) && errorCode.includes(':')) {
    errorCode = errorCode.split(':')[0].trim();
  }

  const silencedCode = extractAnyBackendErrorCode(backendMessage) || errorCode;
  if (SILENCED_BACKEND_ERROR_CODES.has(silencedCode)) {
    return;
  }

  if (backendMessage) {
    const codeFromMessage = extractKnownBackendErrorCode(backendMessage);
    if (codeFromMessage) {
      toast.error(i18n.t(`backendErrors.${codeFromMessage}`), {
        toastId: codeFromMessage,
      });
      return;
    }
    const unknownCodeFromMessage = extractAnyBackendErrorCode(backendMessage);
    if (unknownCodeFromMessage) {
      toast.error(humanizeBackendErrorCode(unknownCodeFromMessage) || backendMessage, {
        toastId: unknownCodeFromMessage,
      });
      return;
    }
    const genericCreatingFollowUp =
      backendMessage === 'Error creating follow-up' ||
      backendMessage === 'Erro ao criar follow-up' ||
      backendMessage === 'Error al crear follow-up';
    const messageToShow = genericCreatingFollowUp
      ? i18n.t('backendErrors.ERR_CREATING_FOLLOW_UP')
      : backendMessage;
    toast.error(messageToShow, {
      toastId: errorCode || messageToShow.slice(0, 50),
    });
    return;
  }

  if (errorCode) {
    if (i18n.exists(`backendErrors.${errorCode}`)) {
      toast.error(i18n.t(`backendErrors.${errorCode}`), {
        toastId: errorCode,
      });
      return;
    }
    const humanized = humanizeBackendErrorCode(errorCode);
    if (humanized) {
      toast.error(humanized, { toastId: errorCode });
      return;
    }
    toast.error(errorCode, { toastId: errorCode });
    return;
  }

  if (isString(err)) {
    toast.error(err);
    return;
  }

  toast.error(resolveFallbackErrorMessage(err));
};

export default toastError;
